"use client";

import type { FeatherySDK } from "@/app/_components/feathery/FeatheryContext";

import { FeatheryForm } from "@/app/_components/feathery/FeatheryForm";
import { toast } from "@/app/_providers/toast";
import { Button } from "@ui/components/core/Button";
import { cn } from "@ui/lib/utils";
import { ICON_ALIGN_OPTIONS } from "@ui/tokens/icon-types";
import { ChevronLeft } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

import { Dialog, DialogContent } from "./Dialog";

interface RequestVoiceDialogProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

function RequestNewVoiceContent({
  onFormSubmit,
  onStepChange,
}: {
  onFormSubmit: () => void;
  onStepChange: (step: 1 | 2) => void;
}) {
  const t = useTranslations("ClientKeys.RequestVoiceDialog");
  const [step, setStep] = useState<1 | 2>(1);
  const [voiceName, setVoiceName] = useState("");

  const handleNextStep = () => {
    if (voiceName.trim()) {
      setStep(2);
      onStepChange(2);
    }
  };

  const handleBackStep = () => {
    setStep(1);
    onStepChange(1);
  };

  const handleFormLoad = (sdk: FeatherySDK) => {
    sdk.setFieldValues({
      iconic_voices_voice_name: "[New voice] " + voiceName,
    });
  };

  return (
    <div className='flex-1 bg-default p-32 md:overflow-y-auto'>
      {step === 1 ? (
        <div className='flex h-full flex-col'>
          <h3 className='f-heading-04 mb-4'>{t("request_new_voice")}</h3>
          <p className='f-paragraph-02 mb-32 text-light'>{t("enter_voice_name_description")}</p>

          <div className='mb-32'>
            <label className='f-paragraph-02 mb-12 block' htmlFor='voice-name'>
              {t("voice_name_label")}
            </label>
            <input
              className='border-light focus:border-primary f-paragraph-02 w-full rounded-lg border bg-none px-16 py-12 transition-colors placeholder:text-light focus:outline-none'
              id='voice-name'
              onChange={e => setVoiceName(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleNextStep();
                }
              }}
              placeholder={t("voice_name_placeholder")}
              type='text'
              value={voiceName}
            />
          </div>

          <div className='mt-auto'>
            <Button
              className='w-full'
              disabled={!voiceName.trim()}
              onClick={handleNextStep}
              size='lg'
              variant='elevated'
            >
              {t("continue")}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <Button
            className='mb-24'
            icon={<ChevronLeft size={16} />}
            iconPosition={ICON_ALIGN_OPTIONS.BEFORE}
            onClick={handleBackStep}
            size='md'
            variant='elevated'
          >
            {t("back")}
          </Button>
          <h3 className='f-heading-04 mb-4'>{t("request_license")}</h3>
          <p className='f-paragraph-02 mb-32 text-light'>{t("form_description", { voiceName })}</p>
          <div className='min-h-[500px]'>
            <FeatheryForm formId='MCkkW3' fullWidth onLoad={handleFormLoad} onSubmit={onFormSubmit} />
          </div>
        </>
      )}
    </div>
  );
}

export function RequestVoiceDialog({ onOpenChange, open }: RequestVoiceDialogProps) {
  const t = useTranslations("ClientKeys.RequestVoiceDialog");
  const [step, setStep] = useState<1 | 2>(1);

  const handleFormSubmit = () => {
    toast.success(t("form_submitted"));
    onOpenChange(false);
  };

  const handleDialogClose = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      setStep(1);
    }
  };

  const isCompactSize = step === 1;

  return (
    <Dialog onOpenChange={handleDialogClose} open={open}>
      <DialogContent
        className={cn(
          "h-[95vh] max-w-5xl overflow-hidden overflow-y-auto rounded-xl border bg-none p-0 shadow-xs transition-all duration-300 ease-in-out md:overflow-y-hidden",
          isCompactSize ? "max-h-[400px]" : "max-h-[800px]",
        )}
        closeButtonVariant='inside'
        overlayClassName='bg-overlay-dark backdrop-blur-none'
      >
        <div className='flex h-full flex-col md:flex-row'>
          <RequestNewVoiceContent onFormSubmit={handleFormSubmit} onStepChange={setStep} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
